/**
 * Lane planner right pane: the quotes of one search from every vendor, in the
 * Figma sections ("Best options", then one section per mode, or "Other
 * options" for a single mode), with a banner per vendor that is still
 * working, failed, or was skipped. Risk is a Zoomlogi mock score
 * (quoteScores.ts).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { message, Spin } from 'antd';

import type {
  AccessLeg,
  LaneSearch,
  LaneSites,
  NormalizedQuote,
  TruckingLeg,
} from '../../../apis/lanePlanner';
import { AddFilter, FilterChips } from './AddFilter';
import CalendarGrid from './CalendarGrid';
import DayStrip from './DayStrip';
import QuoteCard from './QuoteCard';
import { SearchNotices, SearchProgress } from './SearchStatus';
import SiteFilters from './SiteFilters';
import {
  applySiteFilters,
  laneBudget,
  lanesToAdd,
  MAX_LANES_TOTAL,
  MAX_NEW_LANES_PER_CALL,
  SearchCandidates,
  searchedSites,
  siteGroupsFor,
} from './siteSelection';
import { buildSearchReport, describeGroup, explainEmpty } from './searchReport';
import ResultsToolbar, { ResultsView } from './ResultsToolbar';
import {
  findPackagingOption,
  isTransitOverPackagingBudget,
} from './packagingOptions';
import { buildSections, vendorNameOf } from './quoteView';
import TruckingProviderDrawer from './TruckingProviderDrawer';
import {
  arrivalDayOf,
  choiceKey,
  fitOf,
  TruckingChoices,
} from './truckingView';
import {
  activeFilterCount,
  applyFilters,
  buildCalendar,
  buildDayStrip,
  hasDatedPrices,
  filterByDay,
  filterGroupsFor,
  pruneFilters,
  FilterState,
  sortQuotes,
  SortKey,
} from './resultsView';
import styles from './styles.module.css';

interface OpenCard {
  section: string;
  quoteId: string;
}

/** Title of the top section for each sort. */
const TOP_TITLES: Record<SortKey, string> = {
  best: 'Best options',
  cheapest: 'Cheapest',
  fastest: 'Fastest',
  earliest: 'Earliest departures',
};

interface ResultsPaneProps {
  search: LaneSearch | null;
  searching: boolean;
  error: string | null;
  savedQuoteIds: ReadonlySet<string>;
  savingQuoteIds: ReadonlySet<string>;
  onSaveQuote: (quote: NormalizedQuote) => void;
  onShareQuote: (quote: NormalizedQuote) => void;
  /** The carrier picked for each trucking leg, kept by the page so a pick outlives this pane. */
  truckingChoices: TruckingChoices;
  onChooseTrucking: (legKey: string, carrierId: string) => void;
  onRetryVendors: (vendorSearchIds: string[]) => void;
  /** The boxes as searched; with `onAddLanes` they offer the origin and destination airport and port filters. */
  candidates?: SearchCandidates;
  /** Searches lanes beyond the first ones, when a filter picks sites not searched yet. Resolves false when they were not accepted, so the picks that asked for them are undone. */
  onAddLanes?: (sites: {
    origin_sites: LaneSites;
    destination_sites: LaneSites;
  }) => Promise<boolean>;
  /** Why no search can run (no vendor enabled), shown instead of the invitation to search. */
  blockedReason?: string;
  /** Given only when booking is on; the Book button shows on bookable quotes. */
  onBookQuote?: (quote: NormalizedQuote) => void;
  /** Packaging chosen on the Flight form; flags air quotes over its stability budget. */
  packagingOption?: string | null;
}

const ResultsPane: React.FC<ResultsPaneProps> = ({
  search,
  searching,
  error,
  savedQuoteIds,
  savingQuoteIds,
  onSaveQuote,
  onShareQuote,
  truckingChoices,
  onChooseTrucking,
  onRetryVendors,
  candidates,
  onAddLanes,
  blockedReason,
  onBookQuote,
  packagingOption,
}) => {
  const [expandedQuote, setExpandedQuote] = useState<OpenCard | null>(null);
  const [expandedSections, setExpandedSections] = useState<ReadonlySet<string>>(
    new Set()
  );

  const [filters, setFilters] = useState<FilterState>({});
  const [siteFilters, setSiteFilters] = useState<FilterState>({});
  const [sort, setSort] = useState<SortKey>('best');
  const [view, setView] = useState<ResultsView>('list');
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // The leg last opened stays set while the panel slides out, so it does not blank.
  const [changing, setChanging] = useState<{
    leg: TruckingLeg;
    site: string;
    open: boolean;
    /** The quote it was opened from: which carriers fit depends on its dates. */
    quote: NormalizedQuote;
  } | null>(null);
  const changingKey = changing && choiceKey(changing.leg, changing.site);
  // A retry turns a leg back to pending with no options; the open panel keeps the last ones meanwhile.
  const [lastOptions, setLastOptions] = useState<{
    key: string;
    entry: AccessLeg;
  } | null>(null);
  const liveEntry = changing
    ? search?.access_legs?.[changing.leg][changing.site]
    : undefined;
  useEffect(() => {
    if (changingKey && liveEntry?.options?.length) {
      setLastOptions({ key: changingKey, entry: liveEntry });
    }
  }, [changingKey, liveEntry]);
  const refreshing =
    liveEntry?.status === 'pending' && !liveEntry.options?.length;
  const drawerEntry =
    refreshing && lastOptions?.key === changingKey
      ? lastOptions.entry
      : liveEntry;

  const quotes = search?.quotes;
  const groups = useMemo(
    () => (search ? filterGroupsFor(search, quotes ?? []) : []),
    [search, quotes]
  );
  // A filter whose chip is no longer offered must not keep hiding quotes.
  const activeFilters = useMemo(
    () => pruneFilters(filters, groups),
    [filters, groups]
  );
  const siteGroups = useMemo(
    () => (search && candidates ? siteGroupsFor(search, candidates) : []),
    [search, candidates]
  );
  // a chip with no pick of its own shows, and keeps, the sites the search started with
  const activeSiteFilters = useMemo(() => {
    const picked = pruneFilters(siteFilters, siteGroups);
    return Object.fromEntries(
      siteGroups.map((group) => [
        group.key,
        picked[group.key]?.length ? picked[group.key] : group.defaults,
      ])
    );
  }, [siteFilters, siteGroups]);
  const searchId = search?.id;
  useEffect(() => {
    setSiteFilters({});
  }, [searchId]);
  const filtered = useMemo(
    () =>
      applySiteFilters(
        applyFilters(quotes ?? [], activeFilters),
        activeSiteFilters
      ),
    [quotes, activeFilters, activeSiteFilters]
  );
  const anyFilter =
    activeFilterCount(activeFilters) + activeFilterCount(activeSiteFilters) > 0;
  // Shown on the search's quotes, not the filtered ones, so a filter never hides the strip.
  const strip = useMemo(
    () =>
      search && hasDatedPrices(quotes ?? [])
        ? buildDayStrip(search, filtered)
        : null,
    [search, quotes, filtered]
  );
  // The calendar is only built when it is shown: it is a pass over every quote.
  const calendar = useMemo(
    () =>
      search && view === 'calendar' ? buildCalendar(search, filtered) : null,
    [search, filtered, view]
  );
  // A picked day only applies while the strip it was picked on is shown.
  const activeDay = strip ? selectedDay : null;
  const visible = useMemo(
    () => filterByDay(filtered, activeDay),
    [filtered, activeDay]
  );
  // The calendar shows every day, so a picked day only narrows the list.
  const shownCount = view === 'calendar' ? filtered.length : visible.length;

  const sections = useMemo(() => {
    const build = (
      open: ReadonlySet<string>
    ): ReturnType<typeof buildSections> =>
      buildSections(
        visible,
        open,
        (list) => sortQuotes(list, sort),
        TOP_TITLES[sort]
      );
    const built = build(expandedSections);
    // An open quote pushed out of its section's preview keeps its section expanded.
    const openId = expandedQuote?.quoteId;
    const hidden =
      openId && !built.some((s) => s.quotes.some((q) => q.id === openId))
        ? visible.find((q) => q.id === openId)
        : undefined;
    return hidden ? build(new Set(expandedSections).add(hidden.mode)) : built;
  }, [visible, expandedSections, expandedQuote, sort]);
  const packaging = useMemo(
    () => findPackagingOption(packagingOption),
    [packagingOption]
  );

  const report = useMemo(
    () => (search ? buildSearchReport(search) : null),
    [search]
  );
  const quoteCount = quotes?.length ?? 0;
  const notices = report && (
    <SearchNotices
      report={report}
      quoteCount={quoteCount}
      searching={searching}
      onRetry={onRetryVendors}
    />
  );
  const hasQuotes = quoteCount > 0;

  if (!search && !searching) {
    return (
      <div className={styles.resultsPane}>
        <div className={styles.emptyState}>
          <span className={styles.emptyStateTitle}>No results yet</span>
          <span>
            {blockedReason ??
              'Fill in the lane on the left and hit “Search” to pull live rates.'}
          </span>
          {error && <div className={styles.errorBanner}>{error}</div>}
        </div>
      </div>
    );
  }

  if (quoteCount === 0 && searching) {
    return (
      <div className={styles.resultsPane}>
        <div className={styles.emptyState}>
          <Spin />
          <span className={styles.emptyStateTitle}>Searching vendors…</span>
          <span>Live rates stream in as vendors respond.</span>
          {report?.groups
            .filter((group) => group.kind === 'pending')
            .map((group) => (
              <div key={group.key} className={styles.muted}>
                {describeGroup(group)}
              </div>
            ))}
        </div>
      </div>
    );
  }

  // The open card is the quote clicked, in the section it was clicked in: a top
  // quote also sits in its mode section, and only that card should open. When
  // polling moves the quote to another section it stays open there.
  const openSection =
    expandedQuote &&
    (
      sections.find(
        (s) =>
          s.key === expandedQuote.section &&
          s.quotes.some((q) => q.id === expandedQuote.quoteId)
      ) ??
      sections.find((s) => s.quotes.some((q) => q.id === expandedQuote.quoteId))
    )?.key;
  const toggleQuote = (section: string, quoteId: string): void =>
    setExpandedQuote((current) =>
      current?.quoteId === quoteId && section === openSection
        ? null
        : { section, quoteId }
    );

  const applySites = async (next: FilterState): Promise<void> => {
    const previous = siteFilters;
    setSiteFilters(next);
    const lanes = search && onAddLanes ? lanesToAdd(search, next) : null;
    if (!lanes || !onAddLanes) return;
    const { newLanes, totalLanes } = laneBudget(search as LaneSearch, lanes);
    if (newLanes > MAX_NEW_LANES_PER_CALL || totalLanes > MAX_LANES_TOTAL) {
      message.warning(
        `That would search ${newLanes} new lanes (${totalLanes} in all). At most ${MAX_NEW_LANES_PER_CALL} can be added at once and ${MAX_LANES_TOTAL} searched per mode: pick fewer airports or ports.`
      );
      setSiteFilters(previous);
      return;
    }
    // Picks that need lanes the server did not take would hide every quote
    // on the other sites, so they are undone.
    if (!(await onAddLanes(lanes))) setSiteFilters(previous);
  };

  const clearFilters = (): void => {
    setFilters({});
    // "Clear" shows every lane searched, including those added after the start
    setSiteFilters(
      search
        ? Object.fromEntries(
            siteGroups.map((group) => [
              group.key,
              searchedSites(search, group.end, group.kind),
            ])
          )
        : {}
    );
  };

  const showMore = (key: string): void =>
    setExpandedSections((current) => new Set(current).add(key));

  /** Shows a calendar cell's quote expanded in the list. */
  const openQuote = (quote: NormalizedQuote): void => {
    setView('list');
    setSelectedDay(null);
    const opened = new Set(expandedSections).add(quote.mode);
    setExpandedSections(opened);
    const target = buildSections(
      filtered,
      opened,
      (list) => sortQuotes(list, sort),
      TOP_TITLES[sort]
    ).find((section) => section.quotes.some((q) => q.id === quote.id));
    setExpandedQuote({ section: target?.key ?? 'best', quoteId: quote.id });
  };

  return (
    <div className={styles.resultsPane}>
      <div className={styles.resultsHeader}>
        <h3 className={styles.resultsTitle}>Rates</h3>
        <span className={styles.resultsSub}>
          {shownCount} option{shownCount === 1 ? '' : 's'}
          {shownCount !== quoteCount ? ` of ${quoteCount}` : ''}
        </span>
      </div>
      {report && <SearchProgress report={report} searching={searching} />}
      {error && (
        <div className={`${styles.errorBanner} ${styles.bannerSpaced}`}>
          {error}
        </div>
      )}
      {quoteCount === 0 && !searching && report && (
        <div className={styles.emptyState}>
          <span className={styles.emptyStateTitle}>
            {explainEmpty(report).title}
          </span>
          <span>{explainEmpty(report).hint}</span>
          {notices}
        </div>
      )}

      {/* site chips also with no rates: another nearby airport or port is the way forward */}
      {(hasQuotes || siteGroups.length > 0) && (
        <ResultsToolbar
          display={
            hasQuotes
              ? {
                  sort,
                  onSortChange: setSort,
                  view,
                  onViewChange: setView,
                }
              : undefined
          }
        >
          {hasQuotes && (
            <AddFilter
              groups={groups}
              value={activeFilters}
              onChange={setFilters}
            />
          )}
          <SiteFilters
            groups={siteGroups}
            value={activeSiteFilters}
            onChange={applySites}
          />
          {hasQuotes && (
            <FilterChips
              groups={groups}
              value={activeFilters}
              onChange={setFilters}
            />
          )}
        </ResultsToolbar>
      )}

      {hasQuotes && view === 'list' && strip && (
        <DayStrip
          strip={strip}
          selectedDay={selectedDay}
          onSelect={setSelectedDay}
        />
      )}

      <div className={styles.resultsScroll}>
        {view === 'list' && quoteCount > 0 && visible.length === 0 && (
          <div className={styles.emptyState}>
            <span>
              None of the {quoteCount} rates match
              {anyFilter ? ' the filters' : ''}
              {activeDay ? ' on that day' : ''}.
            </span>
            <span>
              {anyFilter && (
                <button
                  type='button'
                  className={styles.detailToggle}
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              )}
              {activeDay && (
                <button
                  type='button'
                  className={styles.detailToggle}
                  onClick={() => setSelectedDay(null)}
                >
                  Show all days
                </button>
              )}
            </span>
          </div>
        )}
        {view === 'calendar' && calendar && (
          <CalendarGrid calendar={calendar} onOpenQuote={openQuote} />
        )}
        {view === 'list' &&
          sections.map((section) => (
            <section key={section.key} aria-label={section.title}>
              <h4 className={styles.sectionHeading}>{section.title}</h4>
              <div className={styles.rateList}>
                {section.quotes.map((quote) => (
                  <QuoteCard
                    key={quote.id}
                    quote={quote}
                    vendorName={vendorNameOf(
                      search as LaneSearch,
                      quote.vendor
                    )}
                    expanded={
                      openSection === section.key &&
                      expandedQuote?.quoteId === quote.id
                    }
                    onToggle={() => toggleQuote(section.key, quote.id)}
                    overBudget={
                      quote.mode === 'air' &&
                      isTransitOverPackagingBudget(
                        quote.transit_minutes,
                        packaging
                      )
                    }
                    shipDate={search?.ship_date}
                    accessLegs={search?.access_legs}
                    truckingChoices={truckingChoices}
                    onChangeTrucking={(leg, site) => {
                      setChanging({
                        leg,
                        site,
                        open: true,
                        quote,
                      });
                    }}
                    hideRoadLane
                    saved={savedQuoteIds.has(quote.id)}
                    saving={savingQuoteIds.has(quote.id)}
                    onSave={() => onSaveQuote(quote)}
                    onShare={() => onShareQuote(quote)}
                    onBook={
                      onBookQuote && quote.bookable
                        ? () => onBookQuote(quote)
                        : undefined
                    }
                  />
                ))}
              </div>
              {section.hidden > 0 && (
                <button
                  type='button'
                  className={styles.seeMore}
                  onClick={() => showMore(section.key)}
                >
                  See more ({section.hidden})
                </button>
              )}
            </section>
          ))}
        {hasQuotes && notices}
      </div>
      <TruckingProviderDrawer
        open={!!changing?.open}
        leg={changing?.leg ?? 'pickup'}
        entry={drawerEntry}
        refreshing={refreshing}
        shipDate={search?.ship_date}
        arrivalDay={changing && arrivalDayOf(changing.quote)}
        fits={
          changing
            ? fitOf(changing.quote, changing.leg, search?.ship_date)
            : undefined
        }
        selectedId={changingKey ? truckingChoices[changingKey] : undefined}
        onSelect={(id) => changingKey && onChooseTrucking(changingKey, id)}
        onClose={() => setChanging((prev) => prev && { ...prev, open: false })}
      />
    </div>
  );
};

export default ResultsPane;
